import { mediaApi } from '../api/media';
import { chatApi } from '../api/chat';
import { uploadChatVideo } from '../pages/ChatRoomPage/uploadChatVideo';
import { saveDraftBinary, getDraftBinary, removeDraftBinaries } from '../utils/creationDrafts';

const KEY = 'amposter:chat-video-queue:v1';
export const CHAT_VIDEO_EVENT = 'amposter:chat-video-queue';
const running = new Map();
let activeOwner = null;
export const getChatVideoTasks = () => {
  try { const value = JSON.parse(localStorage.getItem(KEY) || '[]'); return Array.isArray(value) ? value : []; }
  catch { return []; }
};
const write = tasks => {
  localStorage.setItem(KEY, JSON.stringify(tasks));
  window.dispatchEvent(new Event(CHAT_VIDEO_EVENT));
};
const patch = (id, changes) => write(getChatVideoTasks().map(t => t.id === id ? { ...t, ...changes } : t));
const stage = job => ({ AWAITING_UPLOAD: 'UPLOADING', UPLOADED: 'PROCESSING', PROCESSING: 'PROCESSING', READY: 'SENDING' }[job.status] || 'PROCESSING');

export function setChatVideoOwner(owner) {
  activeOwner = owner;
  for (const { ownerId, controller } of running.values()) if (ownerId !== owner) controller.abort();
}

async function processTask(id, file, controller) {
  let task = getChatVideoTasks().find(t => t.id === id);
  if (!task || ['SENT', 'FAILED'].includes(task.stage)) return;
  const checkOwner = () => { if (controller.signal.aborted || activeOwner !== task.ownerId) throw new DOMException('Session changed', 'AbortError'); };
  try {
    checkOwner();
    // An interrupted send may already have reached the server. Never resend it automatically.
    if (task.stage === 'SENDING') {
      patch(id, { stage: 'FAILED', uncertain: true, error: 'تعذر تأكيد الإرسال. تحقق من المحادثة قبل إرسال الفيديو مرة أخرى.' });
      return;
    }
    if (file) {
      const saved = await saveDraftBinary(task.ownerId, id, 'chat-video', file);
      checkOwner();
      if (!saved) throw new Error('تعذر حفظ الفيديو للرفع في الخلفية. تحقق من مساحة التخزين.');
    }
    let job = task.jobId ? await mediaApi.get(task.jobId) : null;
    checkOwner();
    if (job && (job.status === 'CANCELLED' || (job.status === 'FAILED' && !job.nextAttemptAt))) {
      job = null;
      patch(id, { jobId: null });
    }
    if (job?.status === 'AWAITING_UPLOAD') {
      await mediaApi.cancel(job.id);
      checkOwner();
      job = null;
      patch(id, { jobId: null });
    }
    let attachment;
    if (!job) {
      const source = file || await getDraftBinary(task.ownerId, id, 'chat-video');
      checkOwner();
      if (!source) throw new Error('تعذر استعادة الفيديو. اختر الملف مجدداً.');
      patch(id, { stage: 'UPLOADING', error: null });
      attachment = await uploadChatVideo(source, next => {
        checkOwner();
        patch(id, { jobId: next.id, stage: next.status === 'READY' ? 'PROCESSING' : stage(next) });
      }, controller.signal);
    } else {
      patch(id, { stage: 'PROCESSING' });
      if (job.status !== 'READY') job = await mediaApi.waitUntilReady(job.id, { signal: controller.signal });
      if (!job.publicUrl) throw new Error('الفيديو غير جاهز للإرسال.');
      attachment = { type: 'VIDEO', url: job.publicUrl, contentType: 'video/mp4', fileName: task.fileName.replace(/\.[^.]+$/, '') + '.mp4' };
    }
    checkOwner();
    patch(id, { stage: 'SENDING' });
    const message = await chatApi.send(task.conversationId, { type: 'VIDEO', content: task.caption || null, replyToMessageId: task.replyToMessageId, attachments: [attachment] });
    patch(id, { stage: 'SENT', message, error: null });
    await removeDraftBinaries(task.ownerId, id);
  } catch (error) {
    if (error.name === 'AbortError') return;
    const latest = getChatVideoTasks().find(t => t.id === id);
    patch(id, { stage: 'FAILED', uncertain: latest?.stage === 'SENDING', error: error.message || 'تعذر إرسال الفيديو.' });
  }
}

function run(id, file) {
  const task = getChatVideoTasks().find(t => t.id === id);
  if (!task || task.ownerId !== activeOwner || running.has(id)) return;
  const controller = new AbortController();
  running.set(id, { controller, ownerId: task.ownerId });
  const execute = () => processTask(id, file, controller);
  const work = navigator.locks ? navigator.locks.request(`chat-video:${id}`, execute) : execute();
  void work.catch(error => console.error('Chat video queue failed:', error)).finally(() => {
    running.delete(id);
    if (controller.signal.aborted && activeOwner === task.ownerId) run(id, file);
  });
}

export function enqueueChatVideo({ ownerId, conversationId, route, file, caption, replyToMessageId }) {
  if (!ownerId || !conversationId) throw new Error('المحادثة غير جاهزة.');
  setChatVideoOwner(ownerId);
  const task = { id: crypto.randomUUID(), ownerId, conversationId, route, fileName: file.name, caption, replyToMessageId: replyToMessageId || null, stage: 'UPLOADING', createdAt: new Date().toISOString(), jobId: null };
  write([...getChatVideoTasks(), task]);
  run(task.id, file);
  return task;
}
export function resumeChatVideos(owner) {
  setChatVideoOwner(owner);
  getChatVideoTasks().filter(t => t.ownerId === owner && !['FAILED', 'SENT'].includes(t.stage)).forEach(t => run(t.id));
}
export function retryChatVideo(id) {
  const task = getChatVideoTasks().find(t => t.id === id);
  if (!task || task.ownerId !== activeOwner || task.stage !== 'FAILED' || task.uncertain || running.has(id)) return;
  patch(id, { stage: 'UPLOADING', error: null });
  run(id);
}
export function dismissChatVideo(id) {
  const task = getChatVideoTasks().find(t => t.id === id);
  if (!task || task.ownerId !== activeOwner || !['FAILED', 'SENT'].includes(task.stage)) return;
  write(getChatVideoTasks().filter(t => t.id !== id));
  void removeDraftBinaries(task.ownerId, id);
}
